import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Space, message, Tag, Popconfirm, QRCode } from 'antd';
import { UserAddOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, TeamOutlined, CrownOutlined, UserOutlined, SafetyOutlined, ToolOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { userApi } from '../services/api';
import dayjs from 'dayjs';
import ChannelPermissionSelector from '../components/ChannelPermissionSelector';

export default function UserManagement() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const currentUser = localStorage.getItem('username');
  const [twoFAModalVisible, setTwoFAModalVisible] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  const checkPasswordStrength = (password) => {
    if (!password) { setPasswordStrength({ score: 0, text: '', color: '' }); return; }
    let score = 0, feedback = [];
    if (password.length >= 8) score += 25; else feedback.push(t('atLeast8Chars'));
    if (/[A-Z]/.test(password)) score += 25; else feedback.push(t('includeUppercase'));
    if (/[a-z]/.test(password)) score += 25; else feedback.push(t('includeLowercase'));
    if (/[0-9]/.test(password)) score += 15; else feedback.push(t('includeNumber'));
    if (/[!@#$%^&*(),.?":{}|<>_\-]/.test(password)) score += 10; else feedback.push(t('includeSpecial'));
    let text, color;
    if (score < 50) { text = `${t('passwordStrengthWeak')} - ${feedback.join('、')}`; color = '#ff4d4f'; }
    else if (score < 75) { text = t('passwordStrengthMedium'); color = '#faad14'; }
    else { text = t('passwordStrengthStrong'); color = '#52c41a'; }
    setPasswordStrength({ score, text, color });
  };

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await userApi.getUsers();
      let userData = [];
      if (Array.isArray(response.data)) userData = response.data;
      else if (response.data && Array.isArray(response.data.data)) userData = response.data.data;
      else if (Array.isArray(response)) userData = response;
      setUsers(userData);
    } catch (error) {
      message.error(error.response?.data?.msg || t('loadUsersFailed'));
      setUsers([]);
    } finally { setLoading(false); }
  };

  const handleCreate = () => { setEditingUser(null); form.resetFields(); setPasswordStrength({ score: 0, text: '', color: '' }); setModalVisible(true); };
  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      channelPermissions: user.channel ? { channel: user.channel, currencies: user.currencies || [] } : undefined
    });
    setModalVisible(true);
  };

  const handleDelete = async (userId) => {
    try { await userApi.deleteUser(userId); message.success(t('deactivateSuccess')); loadUsers(); }
    catch (error) { message.error(error.response?.data?.msg || t('operationFailed')); }
  };

  const handleActivate = async (userId) => {
    try { await userApi.activateUser(userId); message.success(t('activateSuccess')); loadUsers(); }
    catch (error) { message.error(error.response?.data?.msg || t('operationFailed')); }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (values.channelPermissions) {
        values.channel = values.channelPermissions.channel;
        values.currencies = values.channelPermissions.currencies;
        delete values.channelPermissions;
      }
      if (editingUser) { await userApi.updateUser(editingUser.id, values); message.success(t('updateSuccess')); }
      else { await userApi.createUser(values); message.success(t('createSuccess')); }
      setModalVisible(false); form.resetFields(); setPasswordStrength({ score: 0, text: '', color: '' }); loadUsers();
    } catch (error) {
      message.error(error.response?.data?.msg || t('operationFailed'));
    } finally { setLoading(false); }
  };

  const handle2FAEnable = async (user) => {
    try { const response = await userApi.enable2FA(user.id); setTwoFAData(response.data); setEditingUser(user); setTwoFAModalVisible(true); }
    catch (error) { message.error(error.response?.data?.msg || t('twoFAEnableFailed')); }
  };

  const handle2FAVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) { message.error(t('enter6Code')); return; }
    try {
      await userApi.verify2FA(editingUser.id, verifyCode);
      message.success(t('twoFAEnabled'));
      setTwoFAModalVisible(false); setVerifyCode(''); setTwoFAData(null); loadUsers();
    } catch (error) { message.error(error.response?.data?.msg || t('twoFAVerifyFailed')); }
  };

  // 角色标签：ADMIN=管理员(金冠)、DEV=开发者(紫色，含普通用户权限 + api-doc 配置管理)、USER=普通用户
  const getRoleTag = (role) => role === 'ADMIN'
    ? <Tag icon={<CrownOutlined />} color="gold">{t('admin')}</Tag>
    : role === 'DEV'
      ? <Tag icon={<ToolOutlined />} color="purple">{t('developer')}</Tag>
      : <Tag icon={<UserOutlined />} color="blue">{t('normalUser')}</Tag>;

  const columns = [
    { title: t('userId'), dataIndex: 'uid', key: 'uid', width: 200, render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>{text}</span> },
    { title: t('username'), dataIndex: 'username', key: 'username', render: (text, record) => <Space>{record.role === 'ADMIN' ? <CrownOutlined style={{ color: '#faad14' }} /> : <UserOutlined />}<span style={{ fontWeight: 500 }}>{text}</span></Space> },
    { title: t('role'), dataIndex: 'role', key: 'role', render: getRoleTag },
    { title: t('userType'), dataIndex: 'userType', key: 'userType', render: (type) => type === 'INTERNAL' ? <Tag color="cyan">🏢 {t('internalUser')}</Tag> : <Tag color="orange">🌐 {t('externalUser')}</Tag> },
    { title: t('channelPermissions'), key: 'channelPermissions',
      render: (_, record) => {
        if (record.role === 'ADMIN' || (!record.channel && (!record.currencies || record.currencies.length === 0))) {
          return record.role === 'ADMIN'
            ? (
              <Tag
                icon={<CrownOutlined />}
                color="gold"
                style={{ fontWeight: 600, padding: '2px 10px', borderRadius: 12 }}
              >
                {t('allPermissions')}
              </Tag>
            )
            : <span style={{ color: '#bbb', fontSize: 13 }}>—</span>;
        }
        return (
          <Space wrap size={4}>
            <Tag
              color="purple"
              style={{ fontWeight: 600, borderRadius: 10, padding: '1px 10px' }}
            >
              {record.channel}
            </Tag>
            {(record.currencies || []).map(c => (
              <Tag key={c} color="blue" style={{ borderRadius: 10, padding: '1px 8px' }}>{c}</Tag>
            ))}
          </Space>
        );
      }
    },
    { title: t('userStatus'), dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'ACTIVE' ? 'success' : 'default'}>{status === 'ACTIVE' ? t('active') : t('disabled')}</Tag> },
    { title: t('createTime'), dataIndex: 'createTime', key: 'createTime', render: (time) => time ? dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss') : '-' },
    {
      title: t('operation'), key: 'action', fixed: 'right', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">{t('edit')}</Button>
          {record.username !== currentUser && record.status === 'ACTIVE' && (
            <Popconfirm title={t('confirmDelete')} onConfirm={() => handleDelete(record.id)} okText={t('confirm')} cancelText={t('cancel')}>
              <Button type="link" danger icon={<StopOutlined />} size="small">{t('deactivate')}</Button>
            </Popconfirm>
          )}
          {record.username !== currentUser && record.status === 'DISABLED' && (
            <Popconfirm title={t('confirmActivate')} onConfirm={() => handleActivate(record.id)} okText={t('confirm')} cancelText={t('cancel')}>
              <Button type="link" icon={<CheckCircleOutlined />} size="small" style={{ color: '#52c41a' }}>{t('activate')}</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><TeamOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('userManagement')}</span></Space>}
          extra={
            <Space size={12}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 140 }}
                options={[
                  { label: <Space size={6}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d9d9d9', display: 'inline-block' }} />{t('all')}</Space>, value: 'ALL' },
                  { label: <Space size={6}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />{t('active')}</Space>, value: 'ACTIVE' },
                  { label: <Space size={6}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d9d9d9', display: 'inline-block' }} />{t('disabled')}</Space>, value: 'DISABLED' },
                ]}
              />
              <Button type="primary" icon={<UserAddOutlined />} onClick={handleCreate} className="primary-button">{t('createUser')}</Button>
            </Space>
          }>
          <Table
            columns={columns}
            dataSource={statusFilter === 'ALL' ? users : users.filter(u => u.status === statusFilter)}
            rowKey="id" pagination={{ pageSize: 10 }} className="modern-table"
          />
        </Card>

        <Modal
          title={<Space><UserAddOutlined style={{ color: '#667eea' }} /><span>{editingUser ? t('editUser') : t('createUser')}</span></Space>}
          open={modalVisible}
          onCancel={() => { setModalVisible(false); form.resetFields(); setPasswordStrength({ score: 0, text: '', color: '' }); }}
          onOk={async () => {
            try {
              const values = await form.validateFields();
              await handleSubmit(values);
            } catch (error) {
              const ef = error.errorFields || [];
              if (ef.length > 0) message.error(`${ef[0].name[0]}: ${ef[0].errors[0]}`);
              else message.error(t('checkForm'));
            }
          }}
          confirmLoading={loading} width={600} className="modern-modal">
          <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={() => message.error(t('checkForm'))} size="large">
            <Form.Item label={`${t('userId')} (UID)`} name="uid" tooltip={t('uidTooltip')}>
              <Input placeholder="U20250102001" disabled style={{ fontFamily: 'monospace', background: '#f5f5f5' }} />
            </Form.Item>
            <Form.Item label={t('username')} name="username"
              rules={[{ required: true, message: t('usernameRequired') }, { min: 3, message: t('usernameMin3') }]}>
              <Input placeholder={t('usernameRequired')} disabled={!!editingUser} />
            </Form.Item>
            {!editingUser && (
              /*
                密码强度校验错误展示修复：
                之前把「密码要求 + 强度条」放在 help 里。Ant Design 中一旦给 Form.Item 传了自定义 help，
                它会「接管」反馈区域，导致 rules 校验失败时的具体错误信息（如「必须包含大写字母」）无法在字段下方显示，
                用户只看到静态要求文字/强度条，感觉「密码不达标却没报错」。
                修复：静态要求 + 强度条改放 extra（extra 不会覆盖校验错误、始终独立展示在错误信息下方），
                help 交还给 antd 自动渲染 rules 的错误信息；validateTrigger 增加 onChange，边输入边即时提示。

                本次报错修复：此处必须用普通 JS 块注释，不能用 JSX 注释（花括号包裹的星号写法）。
                因为这里处于 !editingUser 逻辑与 () 的表达式上下文，括号内需为单个表达式；
                JSX 注释只在 JSX 子元素位置合法，放这里会被解析成对象/代码块，导致语法报错。
              */
              <Form.Item label={t('password')} name="password" validateTrigger={['onChange', 'onBlur']}
                rules={[
                  { required: true, message: t('passwordRequired') },
                  { min: 8, message: t('passwordMin8') }, { max: 32, message: t('passwordMax32') },
                  { pattern: /[A-Z]/, message: t('passwordMustUppercase') },
                  { pattern: /[a-z]/, message: t('passwordMustLowercase') },
                  { pattern: /[0-9]/, message: t('passwordMustNumber') },
                  { pattern: /[!@#$%^&*(),.?":{}|<>_\-]/, message: t('passwordMustSpecial') }
                ]}
                extra={
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{t('passwordRequirements')}</div>
                    {passwordStrength.text && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 4, background: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${passwordStrength.score}%`, height: '100%', background: passwordStrength.color, transition: 'all 0.3s' }} />
                        </div>
                        <span style={{ fontSize: 12, color: passwordStrength.color, fontWeight: 500 }}>{passwordStrength.text}</span>
                      </div>
                    )}
                  </div>
                }>
                <Input.Password placeholder={t('passwordRequired')} onChange={(e) => checkPasswordStrength(e.target.value)} />
              </Form.Item>
            )}
            <Form.Item label={t('role')} name="role" rules={[{ required: true, message: t('pleaseSelect', t('role')) }]} initialValue="USER">
              <Select options={[{ label: `👑 ${t('admin')}`, value: 'ADMIN' }, { label: `🛠️ ${t('developer')}`, value: 'DEV' }, { label: `👤 ${t('normalUser')}`, value: 'USER' }]} />
            </Form.Item>
            <Form.Item label={t('userType')} name="userType" rules={[{ required: true, message: t('pleaseSelect', t('userType')) }]} initialValue="INTERNAL">
              <Select options={[{ label: `🏢 ${t('internalUser')}`, value: 'INTERNAL' }, { label: `🌐 ${t('externalUser')}`, value: 'EXTERNAL' }]} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
              {/* 渠道权限对 USER 与 DEV 都开放：DEV 含普通用户全部权限，同样可按渠道/币种授权；ADMIN 为全渠道权限无需单独配置 */}
              {({ getFieldValue }) => ['USER', 'DEV'].includes(getFieldValue('role')) ? (
                <Form.Item label={t('channelPermissions')} name="channelPermissions" tooltip={t('pleaseSelect', t('channelPermissions'))}>
                  <ChannelPermissionSelector />
                </Form.Item>
              ) : null}
            </Form.Item>
            <Form.Item label={t('userStatus')} name="status" rules={[{ required: true }]} initialValue="ACTIVE">
              <Select options={[{ label: `✅ ${t('active')}`, value: 'ACTIVE' }, { label: `🚫 ${t('disabled')}`, value: 'DISABLED' }]} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={<Space><SafetyOutlined style={{ color: '#52c41a' }} /><span>{t('twoFATitle')}</span></Space>}
          open={twoFAModalVisible}
          onCancel={() => { setTwoFAModalVisible(false); setVerifyCode(''); setTwoFAData(null); }}
          onOk={handle2FAVerify} okText={t('verifyAndEnable')} cancelText={t('cancel')} width={500}>
          {twoFAData && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>{t('twoFAScanDesc')}</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <QRCode value={twoFAData.qrCodeUrl} size={200} />
              </div>
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{t('manualKey')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }}>{twoFAData.secret}</div>
              </div>
              <Form.Item label={t('enter6Code')} style={{ marginBottom: 0 }}>
                <Input placeholder={t('enter6Code')} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)}
                  maxLength={6} size="large" style={{ textAlign: 'center', fontSize: 20, letterSpacing: 8 }} />
              </Form.Item>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
